<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import {
  NCard, NButton, NSpace, NAlert, NDescriptions, NDescriptionsItem,
  NText, NPopconfirm, NSelect, NFormItem, NTag,
} from 'naive-ui'
import { system, docker,
  type VersionResp, type ContainerInfo,
  type SelfUpdateTag, type SelfUpdatePhase, type SelfUpdateRunStatus,
} from '../../api'
import { message } from '../../message'

const version = ref<VersionResp | null>(null)
const stats = ref<{ goroutines: number } | null>(null)
const loadErr = ref<string>('')

const targetVersion = ref<string>('latest')
const targetOptions = ref<Array<{ label: string; value: string }>>([
  { label: 'latest（跟随 master 分支最新 CI 构建）', value: 'latest' },
])
const tagsLoading = ref(false)
const tagsErr = ref<string>('')

const status = ref<SelfUpdateRunStatus | null>(null)
const expanded = ref<Set<string>>(new Set())
const updating = ref(false)
let reloadTimer: number | null = null
// Only reload the page after a self-update WE kicked off in this session.
// Otherwise revisiting the tab with a prior done run would auto-refresh.
let initiatedHere = false
const armRemaining = ref(0)
let armTimer: number | null = null
let statusTimer: number | null = null
const STATUS_POLL_MS = 1500
const HARD_CAP_MS = 5 * 60_000

function errMsg(e: unknown): string {
  if (e instanceof Error) return e.message || 'unknown error'
  if (typeof e === 'string') return e
  try { return JSON.stringify(e) } catch { return String(e) }
}

function fmtAge(iso: string): string {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return iso
  const ago = Math.max(0, Date.now() - t)
  const m = Math.floor(ago / 60000)
  if (m < 1) return '刚刚'
  if (m < 60) return `${m} 分钟前`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} 小时前`
  const d = Math.floor(h / 24)
  return `${d} 天前`
}

function shortDigest(d: string): string {
  const i = d.indexOf(':')
  return i >= 0 ? d.slice(i + 1, i + 1 + 12) : d.slice(0, 12)
}

function tagAge(t: SelfUpdateTag): string {
  if (!t.created || t.created.startsWith('0001')) return '构建时间未知'
  return `${fmtAge(t.created)}（${new Date(t.created).toLocaleString()}）`
}

function isTerminal(s: SelfUpdateRunStatus | null): boolean {
  return !!s && !!s.terminal
}

async function load() {
  loadErr.value = ''
  try { version.value = await system.version() }
  catch (e) { loadErr.value = errMsg(e); message.error(`无法获取版本信息：${loadErr.value}`); return }
  try { stats.value = await system.stats() as any }
  catch (e) { message.error(`无法获取运行时统计：${errMsg(e)}`) }
}

async function loadTags(refresh = false) {
  tagsLoading.value = true
  tagsErr.value = ''
  try {
    const tags = await system.selfUpdateTags(refresh)
    if (!tags || tags.length === 0) return
    targetOptions.value = tags.map(t => {
      const note = t.tag === 'latest' ? '（跟随 master 分支最新 CI 构建）' : ''
      return { label: `${t.tag}${note} — ${tagAge(t)}`, value: t.tag }
    })
  } catch (e) {
    tagsErr.value = errMsg(e)
  } finally {
    tagsLoading.value = false
  }
}

async function loadStatus() {
  try {
    const r = await system.selfUpdateStatus()
    if ((r as any).none) { status.value = null; return }
    status.value = r
    if (isTerminal(r)) {
      stopStatusPoll()
      if (r.terminal === 'done' && initiatedHere && reloadTimer === null) {
        reloadTimer = window.setTimeout(() => window.location.reload(), 1500)
      }
    }
  } catch { /* sm may be mid-restart; keep polling */ }
}

function startStatusPoll() {
  stopStatusPoll()
  const start = Date.now()
  statusTimer = window.setInterval(() => {
    if (Date.now() - start > HARD_CAP_MS) { stopStatusPoll(); return }
    loadStatus()
  }, STATUS_POLL_MS)
}

function stopStatusPoll() {
  if (statusTimer) { window.clearInterval(statusTimer); statusTimer = null }
}

function togglePhase(runId: string, phase: string) {
  const k = `${runId}/${phase}`
  if (expanded.value.has(k)) expanded.value.delete(k)
  else expanded.value.add(k)
  expanded.value = new Set(expanded.value)
}
function phaseExpanded(runId: string, phase: string): boolean {
  return expanded.value.has(`${runId}/${phase}`)
}

function phaseColor(p: SelfUpdatePhase): string {
  switch (p.status) {
    case 'ok':    return 'success'
    case 'fail':  return 'error'
    case 'skip':  return 'default'
    case 'start': return 'info'
    default:      return 'default'
  }
}
function phaseLabel(p: SelfUpdatePhase): string {
  const dur = p.duration_ms ? ` · ${p.duration_ms}ms` : ''
  return `${p.phase} (${p.status})${dur}`
}

onMounted(async () => {
  load()
  loadTags()
  await loadStatus()
  if (status.value && !isTerminal(status.value)) startStatusPoll()
})
onUnmounted(() => {
  if (armTimer) window.clearInterval(armTimer)
  if (reloadTimer !== null) { window.clearTimeout(reloadTimer); reloadTimer = null }
  stopStatusPoll()
})

async function prune() {
  try {
    const r = await docker.prune()
    message.success(`已回收磁盘空间约 ${Math.round(r.bytes_freed / (1024 * 1024))} MB`)
  } catch (e) { message.error(`prune 失败：${errMsg(e)}`) }
}

function startArmCountdown() {
  armRemaining.value = 30
  if (armTimer) window.clearInterval(armTimer)
  armTimer = window.setInterval(() => {
    armRemaining.value -= 1
    if (armRemaining.value <= 0) {
      if (armTimer) { window.clearInterval(armTimer); armTimer = null }
      armRemaining.value = 0
    }
  }, 1000)
}
function cancelArm() {
  if (armTimer) { window.clearInterval(armTimer); armTimer = null }
  armRemaining.value = 0
}

async function selfUpdate() {
  updating.value = true
  try {
    const r = await system.selfUpdate(targetVersion.value.trim())
    if (r.armed) {
      startArmCountdown()
    } else {
      cancelArm()
      initiatedHere = true
      // Optimistically show "updating" before any /status round-trip. The
      // sidecar tears down sm within ~1–3s; if the follow-up /status request
      // loses that race it fails silently, and without this seed the UI
      // would sit on a stale terminal status until the new sm comes back.
      status.value = {
        run_id: 'pending',
        target_image: targetVersion.value.trim() || 'latest',
        initiator: '',
        started_at: new Date().toISOString(),
        terminal: '',
        phases: [],
      } as SelfUpdateRunStatus
      startStatusPoll()
      loadStatus()
    }
  } catch (e) {
    const m = errMsg(e)
    if (m.toLowerCase().includes('sm_self_image')) {
      message.error('SM_SELF_IMAGE 未配置：请填入完整镜像引用')
    } else {
      message.error(`自更新失败：${m}`)
    }
  } finally {
    updating.value = false
  }
}

function container(): ContainerInfo | undefined { return version.value?.container }
</script>

<template>
  <NCard title="运行信息（System info）">
    <template #header-extra>
      <NText depth="3" style="font-size:12px">/api/system/version + /api/system/stats</NText>
    </template>
    <NDescriptions v-if="version" :column="1" label-placement="left" bordered>
      <NDescriptionsItem v-if="container()" label="运行镜像">
        <NText style="font-family:monospace">{{ container()!.image_ref }}</NText>
        <NText depth="3" style="font-size:12px;margin-left:8px">@{{ shortDigest(container()!.image_digest) }}</NText>
      </NDescriptionsItem>
      <NDescriptionsItem v-if="container()?.image_created_at" label="镜像构建于">
        {{ fmtAge(container()!.image_created_at) }}
        <NText depth="3" style="font-size:12px;margin-left:8px">{{ new Date(container()!.image_created_at).toLocaleString() }}</NText>
      </NDescriptionsItem>
      <NDescriptionsItem v-if="container()?.container_started_at" label="容器启动于">
        {{ fmtAge(container()!.container_started_at) }}
        <NText depth="3" style="font-size:12px;margin-left:8px">{{ new Date(container()!.container_started_at).toLocaleString() }}</NText>
      </NDescriptionsItem>
      <NDescriptionsItem label="Go 运行时">{{ version.go_version }}</NDescriptionsItem>
      <NDescriptionsItem v-if="stats" label="Goroutine 数">
        {{ stats.goroutines }}
        <NText depth="3" style="font-size:12px;margin-left:8px">（当前进程内的协程数，可用作粗略的活跃度指标）</NText>
      </NDescriptionsItem>
    </NDescriptions>
    <NAlert v-else-if="loadErr" type="error" :show-icon="true">
      读取运行信息失败：{{ loadErr }}
      <NButton size="small" style="margin-left:12px" @click="load">重试</NButton>
    </NAlert>
    <NText v-else depth="3">加载中…</NText>
  </NCard>

  <NCard style="margin-top:16px" title="维护（Maintenance）">
    <NText depth="3" style="display:block;margin-bottom:12px;font-size:13px">
      执行 <code>docker system prune</code>：清理悬空镜像、停止的容器、未被引用的网络与构建缓存。<b>不会</b>删除正在运行的服务或仍被引用的镜像。
    </NText>
    <NSpace>
      <NPopconfirm @positive-click="prune">
        <template #trigger>
          <NButton>清理 Docker 无用资源（docker prune）</NButton>
        </template>
        将清理悬空镜像、停止的容器、未使用的网络和构建缓存。继续？
      </NPopconfirm>
    </NSpace>
  </NCard>

  <NCard style="margin-top:16px" title="自更新（Self-update）">
    <NAlert type="info" show-icon style="margin-bottom: 12px">
      <template #header>工作机制（两步确认 + sidecar 热替换）</template>
      第一次点击会进入「预备」状态，30 秒内再次点击「确认执行」才会触发。触发后由 sidecar 容器执行 <code>pull → stop → rm → run</code>，复制当前 sm 的环境变量到新容器；失败自动回滚到上一个镜像。
    </NAlert>

    <NAlert v-if="armRemaining > 0" type="warning" show-icon style="margin-bottom:12px">
      <template #header>已预备（armed），等待第二次点击确认</template>
      还剩 <b>{{ armRemaining }}</b> 秒，超时后自动取消。
      <NButton size="tiny" style="margin-left:12px" @click="cancelArm">取消</NButton>
    </NAlert>

    <div v-if="status">
      <NAlert v-if="status.terminal === 'done'" type="success" show-icon style="margin-bottom:12px">
        <template #header>更新完成 ✓</template>
        目标：<code>{{ status.target_image }}</code>
      </NAlert>
      <NAlert v-else-if="status.terminal === 'rollback'" type="warning" show-icon style="margin-bottom:12px">
        <template #header>已回滚到上一个镜像</template>
        新镜像启动失败，已自动回滚，运行未中断。
      </NAlert>
      <NAlert v-else-if="status.terminal === 'fail'" type="error" show-icon style="margin-bottom:12px">
        <template #header>更新失败</template>
        {{ status.note || '请展开各阶段日志查看详情。' }}
      </NAlert>
      <NAlert v-else type="info" show-icon style="margin-bottom:12px">
        <template #header>更新进行中…</template>
        浏览器与 sm 的连接会短暂中断；前端正在轮询状态。
      </NAlert>

      <div class="timeline">
        <div v-for="p in status.phases" :key="p.phase" class="phase-row">
          <NTag :type="(phaseColor(p) as any)" size="small" style="min-width:200px">
            {{ phaseLabel(p) }}
          </NTag>
          <NButton size="tiny" text v-if="p.log && p.log.length" @click="togglePhase(status.run_id, p.phase)">
            {{ phaseExpanded(status.run_id, p.phase) ? '收起' : '展开' }} 日志（{{ p.log.length }} 行）
          </NButton>
          <pre v-if="phaseExpanded(status.run_id, p.phase)" class="phase-log">{{ p.log.join('\n') }}</pre>
        </div>
      </div>
    </div>

    <NFormItem label="目标镜像/版本" :show-feedback="false" style="margin-bottom:6px">
      <NSelect v-model:value="targetVersion" :options="targetOptions"
        filterable tag :loading="tagsLoading"
        placeholder="latest / v1.2.3 / 或直接粘贴完整镜像 ghcr.io/me/sm:tag" />
    </NFormItem>
    <NSpace size="small" style="margin-bottom:12px">
      <NText depth="3" style="font-size:12px">
        下拉显示 GHCR 上对应仓库的所有 tag，按构建时间倒序。也可直接键入任意 tag 或完整镜像引用回车。
      </NText>
      <NButton size="tiny" text :loading="tagsLoading" @click="loadTags(true)">刷新</NButton>
    </NSpace>
    <NAlert v-if="tagsErr" type="warning" :show-icon="false" style="margin-bottom:12px;font-size:12px">
      未能从 registry 拉取 tag 列表：{{ tagsErr }}（仍可手动输入完整镜像引用）
    </NAlert>

    <NSpace align="center">
      <NButton type="primary" :loading="updating" @click="selfUpdate">
        {{ armRemaining > 0 ? `确认执行（${armRemaining}s）` : '尝试自更新' }}
      </NButton>
    </NSpace>
  </NCard>

</template>

<style scoped>
.timeline { margin-bottom: 12px }
.phase-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 6px }
.phase-log {
  width: 100%;
  background: #f6f8fa;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: pre-wrap;
  margin: 4px 0 0 0;
}
</style>
